import BrandLogo from "@/components/BrandLogo";
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import BiometricQuickUnlock from "@/components/BiometricQuickUnlock";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ProviderLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Surface SSO error messages returned from /provider-auth-callback
  useEffect(() => {
    const sso = searchParams.get("sso");
    const msg = searchParams.get("msg");
    if (sso === "domain") {
      setError(msg || "This portal is restricted to @elevatingmindspsychiatry.com Google Workspace accounts.");
    } else if (sso === "error") {
      setError(msg || "Google sign-in failed. Please try again or use your password.");
    } else if (sso === "missing") {
      setError("Google sign-in was cancelled. Please try again.");
    }
    if (sso) {
      // Clean the query string so the error doesn't persist on reload
      const next = new URLSearchParams(searchParams);
      next.delete("sso"); next.delete("msg");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const startGoogleSSO = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/provider-auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      if (u?.role !== "admin") {
        setError("This portal is for providers only. Please use the patient login.");
        setLoading(false);
        return;
      }
      navigate("/provider", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess("");
    try {
      await API.post("/auth/forgot-password", { email });
      setSuccess("If an account exists for that email, a reset code has been sent. Check your inbox.");
      setMode("reset");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess("");
    try {
      await API.post("/auth/reset-password", { email, code: resetCode, new_password: newPassword });
      setSuccess("Password updated! Signing you in…");
      // Auto-login after reset
      setTimeout(async () => {
        try {
          const u = await login(email, newPassword);
          if (u?.role === "admin") navigate("/provider", { replace: true });
          else setMode("login");
        } catch {
          setMode("login"); setSuccess("");
        }
      }, 1200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-primary/10" data-testid="provider-login-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <BrandLogo className="w-56 max-w-full mx-auto mb-4" testId="provider-login-logo" />
          <h1 className="text-3xl font-display font-bold">Provider Sign In</h1>
          <p className="text-sm text-muted-foreground mt-1">Elevated Psychiatry — provider portal</p>
        </div>

        {mode === "login" && (
          <form onSubmit={handleLogin} className="space-y-4 bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="provider-login-form">
            <BiometricQuickUnlock />
            <button
              type="button"
              onClick={startGoogleSSO}
              className="w-full h-12 rounded-full border border-border bg-card hover:bg-accent transition-colors inline-flex items-center justify-center gap-2.5 text-sm font-medium"
              data-testid="provider-google-sso-button"
            >
              <svg aria-hidden="true" className="w-4 h-4" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 34.9 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 39.7 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C41.1 35.1 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">or email</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input data-testid="provider-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@elevatingmindspsychiatry.com" className="mt-1.5 h-12" required />
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input data-testid="provider-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1.5 h-12" required />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="provider-login-error">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <Button data-testid="provider-login-submit" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
            </Button>
            <button
              type="button"
              onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
              className="text-sm text-primary font-medium hover:underline block w-full text-center"
              data-testid="provider-forgot-password-link"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4 bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="provider-forgot-form">
            <button type="button" onClick={() => { setMode("login"); setError(""); setSuccess(""); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to Sign In
            </button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><KeyRound className="w-4 h-4 text-primary" /></div>
              <div>
                <h2 className="font-semibold text-lg">Reset Password</h2>
                <p className="text-xs text-muted-foreground">Enter your provider email to receive a 6-digit reset code.</p>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input data-testid="provider-forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@elevatingmindspsychiatry.com" className="mt-1.5 h-12" required />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <Button data-testid="provider-send-reset-code-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset code"}
            </Button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={handleResetPassword} className="space-y-4 bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="provider-reset-form">
            <button type="button" onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div>
              <h2 className="font-semibold text-lg">Enter reset code</h2>
              <p className="text-xs text-muted-foreground">We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>. Code expires in 15 minutes.</p>
            </div>
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">6-digit code</Label>
              <Input data-testid="provider-reset-code-input" type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="mt-1.5 h-12 text-center text-2xl font-mono tracking-[0.3em]" maxLength={6} required />
            </div>
            <div>
              <Label className="text-sm font-medium">New password</Label>
              <Input data-testid="provider-new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 h-12" minLength={8} required />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <Button data-testid="provider-reset-password-button" type="submit" className="w-full h-12 rounded-full text-base" disabled={loading || resetCode.length < 6}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}

        <div className="text-center space-y-2">
          <Link to="/request-provider-access" className="text-sm text-primary hover:underline font-medium" data-testid="request-provider-access-link">
            Request provider access →
          </Link>
          <div>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-to-patient-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Patient sign in
            </Link>
          </div>
          <div className="pt-1">
            <Link to="/privacy" className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="provider-privacy-policy-link">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
